import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Bot, UserCheck, Mail, CircleDot } from 'lucide-react';

const STATUS_COLOR = {
  open: 'bg-zita-cocoa/10 text-zita-cocoa',
  in_progress: 'bg-ari/10 ari-accent',
  awaiting_user: 'bg-zita-sand text-zita-cocoa',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-zita-taupe/40 text-zita-mocha',
};

const SEVERITY_COLOR = {
  low: 'bg-zita-sand text-zita-mocha',
  normal: 'bg-zita-sand text-zita-cocoa',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const StatusPill = ({ s }) => (
  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[s] || 'bg-zita-sand'}`}>{s || 'open'}</span>
);

const TakeoverBadge = ({ by }) => (
  <span
    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full inline-flex items-center gap-1 ${
      ['founder','super_admin'].includes(by) ? 'bg-emerald-100 text-emerald-800' : 'bg-ari/10 ari-accent'
    }`}
    data-testid={`ticket-takeover-badge-${by}`}
  >
    {['founder','super_admin'].includes(by) ? <UserCheck size={11} /> : <Bot size={11} />}
    {['founder','super_admin'].includes(by) ? 'Human support' : 'Ari handling'}
  </span>
);

export const AdminTickets = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [takeoverFilter, setTakeoverFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({
        status_filter: statusFilter,
        unread_only: unreadOnly ? 'true' : 'false',
        takeover: takeoverFilter,
      });
      const { data } = await api.get(`/admin/tickets?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, unreadOnly, takeoverFilter]);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/admin/tickets/${id}`);
      setSelected(data);
      setReply('');
      // Mark this ticket as read (best-effort, no toast)
      await api.post(`/admin/tickets/${id}/mark_read`).catch(() => {});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/reply`, { text: reply });
      toast.success('Reply sent — delivered in-app and via email.');
      setReply('');
      open(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const takeover = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/takeover`);
      toast.success('You have taken over from Ari.');
      open(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const returnToAri = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/return_to_ari`);
      toast.success('Conversation returned to Ari.');
      open(selected.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/tickets/${id}`, { status });
      toast.success(`Marked ${status}`);
      if (selected?.id === id) open(id);
      else load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl text-zita-espresso">Support</h1>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-zita-mocha flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              data-testid="admin-tickets-unread-only"
              className="accent-zita-espresso"
            />
            Unread only
          </label>
          <select
            data-testid="admin-tickets-takeover"
            value={takeoverFilter}
            onChange={(e) => setTakeoverFilter(e.target.value)}
            className="zita-input px-3 py-2 text-sm"
          >
            <option value="all">Any handler</option>
            <option value="ari">Ari handling</option>
            <option value="super_admin">Human support</option>
          </select>
          <select
            data-testid="admin-tickets-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="zita-input px-3 py-2 text-sm"
          >
            {['all', 'open', 'in_progress', 'awaiting_user', 'resolved', 'closed'].map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="zita-card divide-y divide-zita-taupe/40" data-testid="admin-tickets-list">
          {busy && !items.length && <div className="px-4 py-6 text-center text-zita-mocha text-sm">Loading…</div>}
          {!busy && !items.length && <div className="px-4 py-6 text-center text-zita-mocha text-sm">No tickets in this view.</div>}
          {items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => open(t.id)}
              className={`w-full text-left px-4 py-3 hover:bg-zita-sand/40 ${selected?.id === t.id ? 'bg-zita-sand/60' : ''}`}
              data-testid={`admin-ticket-row-${t.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-zita-espresso truncate flex items-center gap-2">
                  {t.unread_count > 0 && (
                    <span
                      className="w-2 h-2 rounded-full bg-red-500 inline-block"
                      title={`${t.unread_count} unread`}
                      data-testid={`admin-ticket-unread-dot-${t.id}`}
                    />
                  )}
                  <span className="truncate">{t.subject}</span>
                </div>
                <StatusPill s={t.status} />
              </div>
              <div className="text-xs text-zita-mocha mt-1 flex items-center gap-2 flex-wrap">
                <span>{t.user_email}</span>
                <span>·</span>
                <span className={`px-2 py-0.5 rounded-full ${SEVERITY_COLOR[t.severity] || ''}`}>{t.severity}</span>
                <span>·</span>
                <TakeoverBadge by={t.takeover_by || 'ari'} />
                {t.unread_count > 0 && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1 text-red-700">
                      <CircleDot size={11} /> {t.unread_count} new
                    </span>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="zita-card p-5" data-testid="admin-ticket-detail">
          {!selected && <div className="text-sm text-zita-mocha">Select a ticket to view the conversation.</div>}
          {selected && (
            <div>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-serif text-2xl text-zita-espresso">{selected.subject}</h2>
                <div className="flex items-center gap-2">
                  <TakeoverBadge by={selected.takeover_by || 'ari'} />
                  <StatusPill s={selected.status} />
                </div>
              </div>
              <div className="text-xs text-zita-mocha mt-1">
                {selected.user_email} · {selected.severity} · {selected.category}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {!['founder','super_admin'].includes(selected.takeover_by) ? (
                  <button
                    type="button"
                    onClick={takeover}
                    data-testid="admin-ticket-takeover-btn"
                    className="zita-btn-primary text-xs inline-flex items-center gap-1"
                  >
                    <UserCheck size={12} /> Take over from Ari
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={returnToAri}
                    data-testid="admin-ticket-return-btn"
                    className="zita-btn-secondary text-xs inline-flex items-center gap-1"
                  >
                    <Bot size={12} /> Return to Ari
                  </button>
                )}
              </div>
              <div className="mt-4 space-y-3 max-h-96 overflow-y-auto pr-1">
                {(selected.messages || []).map((m, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg ${
                      ['founder','super_admin'].includes(m.author) ? 'bg-zita-espresso text-zita-cream ml-6' :
                      m.author === 'ari' ? 'bg-ari/10 ari-accent' :
                      'bg-zita-sand text-zita-espresso mr-6'
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1 flex items-center gap-2">
                      <span>
                        {['founder','super_admin'].includes(m.author) ? 'You (Zita Support)' : m.author === 'ari' ? 'Ari' : 'User'}
                      </span>
                      <span>·</span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                      {m.via_email && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zita-taupe/40 text-zita-cocoa">
                          <Mail size={10} /> via email
                        </span>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  data-testid="admin-ticket-reply-input"
                  placeholder={
                    ['founder','super_admin'].includes(selected.takeover_by)
                      ? 'Reply as Zita Support (delivered in-app and via email)…'
                      : 'Reply as Zita Support — this will automatically take over from Ari.'
                  }
                  className="zita-input w-full px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2 justify-end">
                  {['in_progress', 'resolved', 'closed'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(selected.id, s)}
                      className="zita-btn-secondary text-xs"
                      data-testid={`admin-ticket-status-${s}`}
                    >
                      Mark {s.replace('_', ' ')}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={!reply.trim()}
                    data-testid="admin-ticket-reply-submit"
                    className="zita-btn-primary text-xs"
                  >
                    Send reply
                  </button>
                </div>
              </div>
              {selected.context && Object.keys(selected.context).length > 0 && (
                <details className="mt-4 text-xs text-zita-mocha">
                  <summary className="cursor-pointer">Diagnostic context</summary>
                  <pre className="bg-zita-sand/40 p-3 rounded-lg overflow-x-auto mt-2">{JSON.stringify(selected.context, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTickets;
